import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Calendar } from './pages/Calendar';
import { Services } from './pages/Services';
import { Availability } from './pages/Availability';
import { WeeklyReport } from './pages/WeeklyReport';
import { Analytics } from './pages/Analytics';
import { Expenses } from './pages/Expenses';
import { Messaging } from './pages/Messaging';
import { Holidays } from './pages/Holidays';
import { Billing } from './pages/Billing';
import { Contacts } from './pages/Contacts';
import { Enquiries } from './pages/Enquiries';
import { RequestSupplies } from './pages/RequestSupplies';
import { SupplyRequests } from './pages/SupplyRequests';
import { KnowledgeBase } from './pages/KnowledgeBase';
import { Referrals } from './pages/Referrals';
// Campaigns now lives inside Messaging (one hub). /campaigns redirects there,
// keeping any query (e.g. ?open=<id> deep links from a contact).
function CampaignsRedirect() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  params.set('tab', 'campaigns');
  return <Navigate to={`/messaging?${params.toString()}`} replace />;
}
import { Quotes } from './pages/Quotes';
import { ServiceLog } from './pages/ServiceLog';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // Contractors are read-only and only see their own schedule (the calendar).
  if (user?.role !== 'ADMIN') {
    return <Navigate to="/calendar" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/calendar" replace />} />
        <Route path="calendar" element={<Calendar />} />
        <Route
          path="services"
          element={
            <AdminRoute>
              <Services />
            </AdminRoute>
          }
        />
        {/* Availability is accessible to contractors (own) and admins (all). */}
        <Route path="availability" element={<Availability />} />
        {/* Contractors and admins can request supplies. */}
        <Route path="request-supplies" element={<RequestSupplies />} />
        <Route
          path="supplies"
          element={
            <AdminRoute>
              <SupplyRequests />
            </AdminRoute>
          }
        />
        <Route
          path="reports"
          element={
            <AdminRoute>
              <WeeklyReport />
            </AdminRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <AdminRoute>
              <Analytics />
            </AdminRoute>
          }
        />
        <Route
          path="billing"
          element={
            <AdminRoute>
              <Billing />
            </AdminRoute>
          }
        />
        <Route
          path="contacts"
          element={
            <AdminRoute>
              <Contacts />
            </AdminRoute>
          }
        />
        <Route
          path="knowledge-base"
          element={
            <AdminRoute>
              <KnowledgeBase />
            </AdminRoute>
          }
        />
        <Route
          path="referrals"
          element={
            <AdminRoute>
              <Referrals />
            </AdminRoute>
          }
        />
        <Route path="campaigns" element={<CampaignsRedirect />} />
        <Route
          path="quotes"
          element={
            <AdminRoute>
              <Quotes />
            </AdminRoute>
          }
        />
        <Route
          path="enquiries"
          element={
            <AdminRoute>
              <Enquiries />
            </AdminRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <AdminRoute>
              <Expenses />
            </AdminRoute>
          }
        />
        <Route
          path="messaging"
          element={
            <AdminRoute>
              <Messaging />
            </AdminRoute>
          }
        />
        <Route
          path="holidays"
          element={
            <AdminRoute>
              <Holidays />
            </AdminRoute>
          }
        />
        <Route
          path="service-log"
          element={
            <AdminRoute>
              <ServiceLog />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
}
